// src/pages/customer/OrderDetail.jsx
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../context/AuthContext";
import { addReview, getReviewsForOrder } from "../../services/firestoreService";
import ReviewModal from "../../components/customer/Reviewmodal";
import Spinner from "../../components/common/Spinner";
import { format } from "date-fns";
import toast from "react-hot-toast";
import {
  FiArrowLeft, FiMapPin, FiPhone, FiMail, FiPackage,
  FiCheck, FiTruck, FiHome, FiCreditCard, FiStar, FiEdit2,
} from "react-icons/fi";

const STATUS_STEPS = ["Pending", "Confirmed", "Shipped", "Delivered"];

const STATUS_COLORS = {
  Pending:   "bg-yellow-100 text-yellow-700 border-yellow-200",
  Confirmed: "bg-blue-100 text-blue-700 border-blue-200",
  Shipped:   "bg-indigo-100 text-indigo-700 border-indigo-200",
  Delivered: "bg-green-100 text-green-700 border-green-200",
  Cancelled: "bg-red-100 text-red-700 border-red-200",
};

const STATUS_DOT = {
  Pending:   "bg-yellow-400",
  Confirmed: "bg-blue-400",
  Shipped:   "bg-indigo-400",
  Delivered: "bg-green-500",
  Cancelled: "bg-red-400",
};

// "30 kg" / "5 pack(s)" depending on the order item's unit
function getQtyLabel(item) {
  if (item.unit === "pack") return item.packSize ? `${item.packSize} pack(s)` : null;
  return item.weightSize ? `${item.weightSize} kg` : null;
}

// "₹107/kg" / "₹99/pack" depending on the order item's unit
function getRateLabel(item) {
  if (item.unit === "pack") return item.pricePerPack ? `₹${item.pricePerPack}/pack` : null;
  return item.pricePerKg ? `₹${item.pricePerKg}/kg` : null;
}

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder]  = useState(null);
  const [loading, setLoading] = useState(true);

  // Reviews the user already left for this order, keyed by productId, so
  // items that were already rated show "Edit Review" pre-filled instead of
  // a blank "Add Review" form.
  const [reviews, setReviews] = useState({});
  const [reviewItem, setReviewItem] = useState(null); // the order item currently open in the modal
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    getDoc(doc(db, "orders", id)).then((snap) => {
      if (snap.exists()) setOrder({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
  }, [id]);

  // Only delivered orders can be reviewed, so only fetch review state then
  useEffect(() => {
    if (order?.status !== "Delivered") return;
    getReviewsForOrder(order.id).then((list) => {
      const map = {};
      list.forEach((r) => { map[r.productId] = r; });
      setReviews(map);
    });
  }, [order]);

  const handleReviewSubmit = async (rating, comment) => {
    if (!reviewItem) return;
    setSubmittingReview(true);
    try {
      await addReview(order.id, reviewItem.id, {
        rating,
        comment,
        userId: user?.uid || null,
        userName: user?.displayName || user?.email || "Customer",
        productName: reviewItem.name,
        productImage: reviewItem.image || "",
      });
      setReviews((prev) => ({
        ...prev,
        [reviewItem.id]: { ...prev[reviewItem.id], rating, comment, productId: reviewItem.id },
      }));
      toast.success("Thanks for your review!");
      setReviewItem(null);
    } catch (err) {
      toast.error("Couldn't save review: " + err.message);
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) return <Spinner center />;

  if (!order) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
      <div className="w-20 h-20 bg-gray-50 rounded-3xl flex items-center justify-center text-4xl mx-auto mb-5">📦</div>
      <h2 className="text-xl font-bold text-gray-700 mb-2">Order not found</h2>
      <p className="text-gray-400 text-sm mb-6">It may have been removed or the link is incorrect.</p>
      <Link to="/orders" className="btn-primary inline-flex items-center gap-2">
        <FiArrowLeft className="w-4 h-4" /> Back to Orders
      </Link>
    </div>
  );

  const stepIdx = STATUS_STEPS.indexOf(order.status);

  return (
    <div className="min-h-screen" style={{ background: "#f7f6f2" }}>
      <Helmet>
        <title>Order Details | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        {/* ── Back link ── */}
        <Link
          to="/orders"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-primary-600 text-sm mb-6 transition-colors"
        >
          <FiArrowLeft className="w-4 h-4" /> Back to Orders
        </Link>

        {/* ── Header card ── */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 mb-5 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Order Details</h1>
            <p className="font-mono text-xs text-gray-400 mt-1.5 tracking-wide">
              #{order.id.slice(0, 12).toUpperCase()}
            </p>
            <p className="text-sm text-gray-400 mt-0.5">
              {order.createdAt?.toDate ? format(order.createdAt.toDate(), "dd MMMM yyyy, hh:mm a") : "Just now"}
            </p>
          </div>
          <span
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border flex-shrink-0
              ${STATUS_COLORS[order.status] || "bg-gray-100 text-gray-700 border-gray-200"}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[order.status] || "bg-gray-400"}`} />
            {order.status}
          </span>
        </div>

        {/* ── Progress tracker ── */}
        {order.status !== "Cancelled" && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 mb-5">
            <h3 className="font-semibold text-gray-900 mb-5 text-sm flex items-center gap-2">
              <FiTruck className="w-4 h-4 text-primary-500" /> Order Status
            </h3>
            <div className="flex items-center">
              {STATUS_STEPS.map((step, i) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors
                        ${i <= stepIdx
                          ? "bg-primary-600 border-primary-600 text-white shadow-sm shadow-primary-200"
                          : "bg-white border-gray-200 text-gray-300"}`}
                    >
                      {i < stepIdx ? <FiCheck className="w-4 h-4" /> : i + 1}
                    </div>
                    <p className={`text-[11px] mt-1.5 font-medium ${i <= stepIdx ? "text-primary-600" : "text-gray-400"}`}>
                      {step}
                    </p>
                  </div>
                  {i < STATUS_STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 mb-5 rounded-full ${i < stepIdx ? "bg-primary-600" : "bg-gray-200"}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* ── Items ── */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 mb-5">
          <h3 className="font-semibold text-gray-900 mb-4 text-sm flex items-center gap-2">
            <FiPackage className="w-4 h-4 text-primary-500" /> Items Ordered
          </h3>
          <div className="space-y-3">
            {order.items?.map((item, i) => (
              <div
                key={i}
                className="flex gap-3 items-center pb-3 border-b border-gray-50 last:border-0 last:pb-0"
              >
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex-shrink-0">
                  {item.image
                    ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-xl">🌱</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm leading-snug truncate">{item.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Qty {item.qty} × ₹{item.price}</p>
                  {(getQtyLabel(item) || getRateLabel(item)) && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {getQtyLabel(item) && (
                        <span className="inline-block text-[11px] text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-1.5 py-0.5">
                          📦 {getQtyLabel(item)}
                        </span>
                      )}
                      {getRateLabel(item) && (
                        <span className="inline-block text-[11px] text-primary-600 bg-primary-50 border border-primary-100 rounded-md px-1.5 py-0.5">
                          {getRateLabel(item)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Review CTA — only once the order has actually been delivered */}
                  {order.status === "Delivered" && (
                    reviews[item.id] ? (
                      <button
                        onClick={() => setReviewItem(item)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 bg-amber-50 border border-amber-100 rounded-full px-2.5 py-1 mt-2 hover:bg-amber-100 transition-colors"
                      >
                        <FiStar className="w-3 h-3 fill-amber-500 text-amber-500" />
                        You rated {reviews[item.id].rating}/5
                        <FiEdit2 className="w-2.5 h-2.5 ml-0.5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => setReviewItem(item)}
                        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary-700 bg-primary-50 border border-primary-100 rounded-full px-2.5 py-1 mt-2 hover:bg-primary-100 transition-colors"
                      >
                        <FiStar className="w-3 h-3" /> Add Review
                      </button>
                    )
                  )}
                </div>
                <span className="font-bold text-gray-900 text-sm flex-shrink-0">
                  ₹{(item.price * item.qty).toFixed(0)}
                </span>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-2 text-sm">
            <div className="flex justify-between text-gray-500">
              <span>Subtotal</span>
              <span className="text-gray-700 font-medium">₹{Number(order.subtotal || 0).toFixed(2)}</span>
            </div>
            {(() => {
              // Orders placed before the delivery-per-kg / SGST / CGST system
              // only have a flat `shipping` field — fall back to that so old
              // orders still render correctly, without showing empty tax rows.
              const hasNewCharges = order.sgstPercent != null || order.cgstPercent != null || order.deliveryCharge != null;

              if (!hasNewCharges) {
                return (
                  <div className="flex justify-between text-gray-500">
                    <span>Delivery</span>
                    <span className={order.shipping === 0 ? "text-primary-600 font-semibold" : "text-gray-700 font-medium"}>
                      {order.shipping === 0 ? "FREE" : `₹${order.shipping}`}
                    </span>
                  </div>
                );
              }

              const totalKgVal      = order.totalKg != null ? Number(order.totalKg) : 0;
              const deliveryChargeVal = Number(order.deliveryCharge || 0);
              const sgstPercentVal   = order.sgstPercent != null ? Number(order.sgstPercent) : 0;
              const cgstPercentVal   = order.cgstPercent != null ? Number(order.cgstPercent) : 0;
              const sgstAmountVal    = Number(order.sgstAmount || 0);
              const cgstAmountVal    = Number(order.cgstAmount || 0);

              return (
                <>
                  <div className="flex justify-between text-gray-500">
                    <span>Delivery Charge{totalKgVal > 0 ? ` (${totalKgVal.toFixed(2)} kg)` : ""}</span>
                    <span className="text-gray-700 font-medium">₹{deliveryChargeVal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>SGST ({sgstPercentVal}%)</span>
                    <span className="text-gray-700 font-medium">₹{sgstAmountVal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>CGST ({cgstPercentVal}%)</span>
                    <span className="text-gray-700 font-medium">₹{cgstAmountVal.toFixed(2)}</span>
                  </div>
                </>
              );
            })()}
            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
              <span className="font-bold text-gray-900">Total</span>
              <span className="font-bold text-lg text-primary-600">₹{Number(order.total || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* ── Delivery details ── */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6">
          <h3 className="font-semibold text-gray-900 mb-4 text-sm flex items-center gap-2">
            <FiHome className="w-4 h-4 text-primary-500" /> Delivery Details
          </h3>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex gap-2.5">
              <FiMapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <p>
                {order.shippingAddress?.address}, {order.shippingAddress?.city}, {order.shippingAddress?.state} – {order.shippingAddress?.pincode}
              </p>
            </div>
            {order.shippingAddress?.landmark && (
              <div className="flex gap-2.5">
                <FiMapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <p>Landmark: {order.shippingAddress.landmark}</p>
              </div>
            )}
            <div className="flex gap-2.5">
              <FiPhone className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <p>{order.userPhone}</p>
            </div>
            <div className="flex gap-2.5">
              <FiMail className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <p>{order.userEmail}</p>
            </div>

            <div className={`mt-3 flex items-center gap-3 border rounded-xl px-4 py-3 ${
              order.paymentMethod === "COD" || !order.paymentMethod
                ? "bg-primary-50 border-primary-100"
                : "bg-green-50 border-green-100"
            }`}>
              <FiCreditCard className={`w-4 h-4 flex-shrink-0 ${
                order.paymentMethod === "COD" || !order.paymentMethod ? "text-primary-600" : "text-green-600"
              }`} />
              <div className="text-xs">
                {order.paymentMethod === "COD" || !order.paymentMethod ? (
                  <p className="text-primary-700 font-semibold">Payment: Cash on Delivery</p>
                ) : (
                  <>
                    <p className="text-green-700 font-semibold flex items-center gap-1.5">
                      <FiCheck className="w-3.5 h-3.5" /> Payment Successful — Paid Online via Razorpay
                    </p>
                    {order.razorpayPaymentId && (
                      <p className="text-green-600/80 mt-0.5 font-mono">
                        Payment ID: {order.razorpayPaymentId}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Add/Edit review modal */}
      <ReviewModal
        open={!!reviewItem}
        productName={reviewItem?.name}
        productImage={reviewItem?.image}
        initialRating={reviewItem ? (reviews[reviewItem.id]?.rating || 0) : 0}
        initialComment={reviewItem ? (reviews[reviewItem.id]?.comment || "") : ""}
        submitting={submittingReview}
        onClose={() => setReviewItem(null)}
        onSubmit={handleReviewSubmit}
      />
    </div>
  );
}